import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';

import { deleteNodeById } from '../../containers/settingsSlice';
import { NODE_FIELDS } from '../../common/fields';
import * as actions from '../../containers/SilverfortNodes/silverfortNodesActions';

import TextInput from '../Controls/TextInput/TextInput';
import Account from '../Controls/Account/Account';
import Button from '../Controls/Button/Button';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';

import styles from './SilverfortNode.module.css';
import NodeDomain from './NodeDomain';
import ArrayContainer from '../Controls/ArrayContainer/ArrayContainer';
import useFeature from '../../hooks/featureFlags/useFeature';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { httpPost } from '../../../utils';
import { filterFieldsBySystemMode } from '../../common/utils';
import Alert from '../Alert/Alert';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const SilverfortNode = ({
    nodeId,
    fields,
    dispatch,
    isProperAz,
    domainsOptions,
    ldapServerOptions,
    nodeIp,
    nodeQueueId,
    onDelete,
    discardNode,
    allowDelete = true,
    isFromWizard = false
}) => {
    const storeDispatch = useDispatch();
    const [showDeleteMessage, setShowDeleteMessage] = useState(false);
    const multiDomainEnabled = useFeature(FEATURE_FLAGS_NAMES.MultiDomainEnabled);
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);

    const filteredFields = filterFieldsBySystemMode(fields, systemMode);
    const nodeName = filteredFields[NODE_FIELDS.NODE_NAME.ID];
    const httpProxy = filteredFields[NODE_FIELDS.HTTP_PROXY.ID];
    const proxyAccount = filteredFields[NODE_FIELDS.PROXY_ACCOUNT.ID];
    const domains = filteredFields[NODE_FIELDS.DOMAINS.ID];

    const domainsIdInUse = [];
    let isRadiusSet;
    if (domains) {
        for (const domainFields of domains.value) {
            const domainId = domainFields[NODE_FIELDS.DOMAIN.ID].value;
            if (domainId) {
                domainsIdInUse.push(domainId);
            }
        }
        isRadiusSet = domains.value.some((domainFields) => domainFields[NODE_FIELDS.RADIUS_NODE.ID].value);
    }

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.UPDATE_NODE,
                payload: { nodeId, fieldId, value }
            });
        },
        [dispatch, nodeId]
    );

    const dispatchAddDomain = useCallback(() => {
        dispatch({
            type: actions.ADD_NODE_DOMAIN,
            payload: { nodeId, systemMode }
        });
    }, [dispatch, nodeId, systemMode]);

    const dispatchDeleteDomain = useCallback(
        (index) => {
            dispatch({
                type: actions.DELETE_NODE_DOMAIN,
                payload: { nodeId, fieldId: NODE_FIELDS.DOMAINS.ID, index }
            });
        },
        [dispatch, nodeId]
    );

    const deleteNode = async (callback) => {
        try {
            const response = await httpPost(`/Settings/deleteNode`, {
                nodeId
            });
            if (response.data.result) {
                dispatch({
                    type: actions.DELETE_NODE,
                    payload: { nodeId }
                });
                storeDispatch(deleteNodeById({ deletedId: nodeId }));
                if (onDelete) {
                    onDelete();
                }
            } else {
                return callback(false, 'Failed to delete node. Please try again later');
            }
        } catch (err) {
            return callback(false, 'Failed to delete node. Please try again later');
        }
    };

    const nodeDisabled = fields[NODE_FIELDS.NODE_ENABLED.ID] && fields[NODE_FIELDS.NODE_ENABLED.ID].value === false;
    const fieldBeforeProxyExists = nodeName;
    const showProxyDivider = fieldBeforeProxyExists && (httpProxy || proxyAccount);
    const showNodeDetailsDivider = fieldBeforeProxyExists || httpProxy || proxyAccount;
    return (
        <div className={styles.nodeContainer}>
            {nodeDisabled && (
                <div className={styles.enableNodeContainer} styles={{ position: 'relative' }}>
                    <div className={styles.btnContainer}>
                        <Button
                            onClick={() => dispatchUpdate(NODE_FIELDS.NODE_ENABLED.ID, true)}
                            style={{
                                color: '#fff',
                                backgroundColor: '#188ded',
                                fontSize: '20px',
                                fontFamily: 'Saira',
                                borderRadius: 24,
                                boxShadow: '0 15px 10px -10px rgb(0 0 0 / 40%)'
                            }}>
                            <div>
                                <span style={{ marginRight: 5, fontWeight: 300 }}>Configure this Node</span>
                                <i className="fa fa-edit"></i>
                            </div>
                        </Button>
                    </div>
                </div>
            )}
            <div className={nodeDisabled ? styles.nodeDisabled : ''}>
                {!isProperAz && systemMode === SYSTEM_MODES.ON_PREM && (
                    <div className={styles.warningContainer}>
                        <Alert message={'Update node in the Availability Zone section'} />
                    </div>
                )}
                {nodeName && <TextInput field={nodeName} changed={dispatchUpdate} />}

                {showProxyDivider && <div className={styles.horizontalDivider} />}
                {httpProxy && (
                    <TextInput field={httpProxy} changed={dispatchUpdate} placeholder="http://192.168.116.1:80" />
                )}
                {proxyAccount && (
                    <Account
                        field={proxyAccount}
                        changed={dispatchUpdate}
                        header="Username and password can use letters and numbers, but no special characters."
                    />
                )}

                {domains &&
                    domains.value.map((nodeDomainFields, index) => (
                        <ArrayContainer
                            key={`${nodeId}_domain${index}`}
                            header={
                                nodeDomainFields[NODE_FIELDS.DOMAIN.ID].value
                                    ? domainsOptions[nodeDomainFields[NODE_FIELDS.DOMAIN.ID].value].label
                                    : ''
                            }
                            remove={domains.value.length > 1 ? () => dispatchDeleteDomain(index) : null}>
                            <NodeDomain
                                fields={filterFieldsBySystemMode(nodeDomainFields, systemMode)}
                                dispatch={dispatch}
                                index={index}
                                nodeId={nodeId}
                                onDelete={() => {}}
                                domainsOptions={domainsOptions}
                                domainsIdInUse={domainsIdInUse}
                                ldapServerOptions={ldapServerOptions}
                                isRadiusSet={isRadiusSet}
                                multiDomainEnabled={isFeatureActive(multiDomainEnabled) || !domains.value.length}
                            />
                        </ArrayContainer>
                    ))}

                {domains && (isFeatureActive(multiDomainEnabled) || !domains.value.length) && (
                    <Button
                        id={`node-${nodeName?.value}-add-domain`}
                        className={`${isFromWizard ? styles.blueButton : ''} ${styles.addDomainButton}`}
                        onClick={dispatchAddDomain}>
                        + Add Domain
                    </Button>
                )}

                {showNodeDetailsDivider && <div className={styles.horizontalDivider} />}
                <div className={styles.additionalData}>
                    <div>Node ID: {nodeId}</div>
                    <div>Node IP: {nodeIp}</div>
                </div>

                {fields[NODE_FIELDS.NODE_ENABLED.ID] && fields[NODE_FIELDS.NODE_ENABLED.ID].value && (
                    <Button
                        id={`node-${nodeName?.value}-discard-changes`}
                        onClick={discardNode}
                        style={{ color: 'white', backgroundColor: '#188ded', marginRight: '15px' }}>
                        Discard changes
                    </Button>
                )}

                {allowDelete && (
                    <Button
                        id={`node-${nodeName?.value}-delete-node`}
                        onClick={() => setShowDeleteMessage(true)}
                        className="my-5 mx-0"
                        deleteButton>
                        Delete Node
                    </Button>
                )}
                {showDeleteMessage && (
                    <ConfirmationMessage
                        title={'Delete Node'}
                        body={
                            <div>
                                {`Are you sure you want to delete "${
                                    nodeName && nodeName.value ? nodeName.value : 'this node'
                                }" ?`}
                                <span>Delete the Node only after the Node VM has been removed.</span>
                            </div>
                        }
                        approveText={'Delete'}
                        cancelText={'Cancel'}
                        approveStyle={'danger'}
                        OnCancel={() => setShowDeleteMessage(false)}
                        OnApprove={deleteNode}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(SilverfortNode);
